import { CommentTargetType } from "@gtmhub/comments";
import { ITeam } from "@gtmhub/teams";
import { toKeyMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { Comment } from "@webapp/comments/models/comments.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { MENTION_PATTERN } from "@webapp/shared/rich-text-editor/rich-text-editor.utils";
import { IReflectionContributor } from "../models/reflection-contributor-list.models";
import { ReflectionPanel } from "../models/reflection-panel.models";
import { ReflectionNoteType, ReflectionOverviewFilterName, reflectionNoteTypes } from "../models/reflections.models";

export function startWithFieldValue<T extends { [K in keyof T] }>(field: keyof T, valueToStartWith: T[keyof T]): (a: T, b: T) => number {
  return function (a: T, b: T): number {
    return a[field] === valueToStartWith ? -1 : b[field] === valueToStartWith ? 1 : 0;
  };
}

export const extractUniqueTeamMemberIds = (team: ITeam): string[] => {
  return [...new Set([...(team.members || []), ...(team.manager ? [team.manager] : [])])];
};

export const toEnrichedContributorListMember = (member: Assignee, params = { withDotIndicator: false }): IReflectionContributor => ({
  ...member,
  withDotIndicator: params.withDotIndicator,
});

export const getLastMondayAsDate = (): string => {
  return dayjs().startOf("isoWeek").toISOString();
};

export const getCurrentWeeksSundayAsDate = (): string => {
  return dayjs().day(7).toISOString();
};

export const getLastEditableMondayAsDate = (): string => {
  return dayjs().startOf("isoWeek").subtract(7, "days").toISOString();
};

export const createdAtBelongsInCadance = (createdAt: string, cadanceStart: string, cadanceEnd: string): boolean => {
  return dayjs(createdAt).isBetween(cadanceStart, cadanceEnd, "days", "[]");
};

export const getLastDayOfCadence = (cadenceStartDate: string, cadenceDurationInDays: number): string => {
  const startDate = dayjs(cadenceStartDate);
  const endDate = startDate
    .clone()
    .add(cadenceDurationInDays - 1, "day")
    .hour(23)
    .minute(59)
    .second(59);

  return endDate.utc().toISOString();
};

export const getPrevCadenceStartDate = (currCadenceStartDate: string, cadenceDurationInDays: number): string => {
  const currStartDate = dayjs(currCadenceStartDate);
  const prevStartDate = currStartDate
    .clone()
    .subtract(cadenceDurationInDays - 1, "day")
    .startOf("isoWeek");

  return prevStartDate.utc().toISOString();
};

export const getFilteredByNoReflectionLength = (reflection: ReflectionPanel[]): number => {
  return reflection.filter((reflection) => reflection.noReflection)?.length || 0;
};

export const getFilteredByReflectionLength = (reflection: ReflectionPanel[]): number => {
  return reflection.filter((reflection) => !reflection.noReflection)?.length || 0;
};

export const getFilteredByNoteTypeLength = (reflection: ReflectionPanel[], filter: ReflectionOverviewFilterName): number => {
  return reflection.filter((reflection) => toKeyMap("type", reflection.notes)[filter]?.text || toKeyMap("type", reflection.notes)[filter]?.gif?.id)?.length || 0;
};

export const generateCommentTemplate = (targetId: string, targetType: CommentTargetType): Comment => {
  return {
    createdBy: "",
    createdAt: "",
    id: targetId,
    mentioned: [],
    seen: [],
    targetId,
    targetTitle: "",
    targetType,
    text: "",
  };
};

export const getLatestDateFromComments = (comments: Comment[]): string => {
  if (!comments?.length) return "";

  return new Date(Math.max(...comments.map((comment) => new Date(comment.createdAt).getTime()))).toISOString();
};

export const isNoteFilterEnabled = (filter: string): boolean => {
  return reflectionNoteTypes.includes(filter as ReflectionNoteType);
};

export const buildCheckinCommentsCounterAriaLabel = (comments: Comment[]): string => {
  return `${comments.length} comment${comments.length === 1 ? "" : "s"}, last comment `;
};

export const getA11yLabelFromAnswer = (answer: string): string => {
  const regex = new RegExp(MENTION_PATTERN, "g");

  let a11yLabel = answer;
  const matches = a11yLabel.matchAll(regex);
  for (const match of matches) {
    const name = match[1];

    a11yLabel = a11yLabel.replace(match[0], name);
  }
  return a11yLabel;
};
